import { Grid } from "@mui/material";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiFieldGroup from "components/base/UiFieldGroup";
import UiSideModal from "components/base/UiSideModal";
import UiTypography from "components/base/UiTypography";
import ScrollAsyncAutocomplete from "components/ScrollAsyncAutocomplete/ScrollAsyncAutocomplete";
import Decimal from "decimal.js";
import PlantOptions from "modules/plants/PlantOptions";
import { ProposalProduct, ProductType } from "generated/graphql";
import useAddPlantProductToProposalProduct from "hooks/proposals/useAddPlantProductToProposalProduct";
import useAddMaterialNoPlantToProposalProduct from "hooks/proposals/useAddMaterialNoPlantToProposalProduct";
import useProposalProductDetails from "hooks/products/useProposalProductDetails";
import { materialNeededCalculations } from "DDD/action-objects/MaterialNeededCalculations";
import { applicationRateMappings } from "modules/products/data/applicationRateMappings";
import { useTonConversionFactor } from "hooks/utils/useTonConversionFactor";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { NUMERIC_SET_VALUE_AS } from "./formUtils";
import { draftColors } from "./draftDesignTokens";
import CustomSelect from "components/Shared/CustomSelect/CustomSelect";

type AddMaterialDrawerProps = {
  open: boolean;
  onClose: () => void;
  product: ProposalProduct;
};

const noPlantSchema = z.object({
  name: z.string().min(1, "Material name is required"),
  percentage: z.coerce.number().min(0).max(100),
  rate: z.coerce.number().min(0),
  quantityOverride: z.any().optional(),
});

type NoPlantFormData = z.infer<typeof noPlantSchema>;

function NoPlantForm({ product, onClose }: { product: ProposalProduct; onClose: () => void }) {
  const [addMaterialNoPlant, { loading }] = useAddMaterialNoPlantToProposalProduct();

  const materialNeeded =
    product.material?.materialNeededOverride || product.material?.materialNeeded || 0;

  const [calculatedQuantity, setCalculatedQuantity] = useState(Math.round(materialNeeded) || 1);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<NoPlantFormData>({
    resolver: zodResolver(noPlantSchema),
    defaultValues: { name: "", percentage: 100, rate: null },
  });

  const watchedPercentage = watch("percentage");

  useEffect(() => {
    if (watchedPercentage != null && materialNeeded) {
      const calculated = Math.round((materialNeeded * watchedPercentage) / 100);
      setCalculatedQuantity(calculated > 0 ? calculated : 1);
    }
  }, [watchedPercentage, materialNeeded]);

  const onSubmit = async (data: NoPlantFormData) => {
    await addMaterialNoPlant({
      variables: {
        input: {
          proposalProductId: product.id,
          name: data.name,
          percentage: data.percentage,
          rate: Math.round(data.rate * 100),
          quantity: calculatedQuantity,
          quantityOverride: data.quantityOverride,
        },
      },
    });
    reset();
    onClose();
  };

  return (
    <MDBox component="form" onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing="12px">
        <Grid item xs={12}>
          <UiFieldGroup
            label="Material Name"
            value=""
            placeholder="Enter material name"
            inputProps={register("name")}
          />
          {errors.name && (
            <UiTypography uiVariant="body-sm" sx={{ color: draftColors.red, mt: "2px" }}>
              {errors.name.message}
            </UiTypography>
          )}
        </Grid>
        <Grid item xs={6}>
          <UiFieldGroup
            label="Percentage (%)"
            value="100"
            inputProps={register("percentage", NUMERIC_SET_VALUE_AS)}
          />
        </Grid>
        <Grid item xs={6}>
          <UiFieldGroup
            label="Rate"
            value=""
            placeholder="0.00"
            startAdornment="$"
            inputProps={register("rate", NUMERIC_SET_VALUE_AS)}
          />
        </Grid>
        <Grid item xs={6}>
          <UiFieldGroup
            label="Quantity"
            value={String(calculatedQuantity)}
            inputProps={register("quantityOverride", NUMERIC_SET_VALUE_AS)}
          />
        </Grid>
      </Grid>
      <MDBox display="flex" justifyContent="flex-end" mt="16px">
        <UiButton uiVariant="primary-blue" type="submit" disabled={loading}>
          {loading ? "Adding..." : "Add material"}
        </UiButton>
      </MDBox>
    </MDBox>
  );
}

function PlantForm({ product, onClose }: { product: ProposalProduct; onClose: () => void }) {
  const [addPlantProduct, { loading }] = useAddPlantProductToProposalProduct();
  const [selectedPlantId, setSelectedPlantId] = useState("");
  const [selectedPlantProductId, setSelectedPlantProductId] = useState("");

  const defaultValues = useMemo(
    () => ({
      plantId: "",
      plantProductId: "",
      applicationRate: (product.material.attributes as any)?.applicationRate || null,
      tonConversionFactor: null as number | null,
      materialNeeded: null as number | null,
      rate: "",
    }),
    [product]
  );

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ values: defaultValues });

  const watchedApplicationRate = watch("applicationRate");
  const tonConversionFactor = useTonConversionFactor(product, watchedApplicationRate);
  const watchedPlantId = watch("plantId");
  const watchedPlantProductId = watch("plantProductId");

  const [calculatedTons, setCalculatedTons] = useState<number>(0);
  const [plantProductRate, setPlantProductRate] = useState<number | null>(null);

  useEffect(() => {
    setValue("tonConversionFactor", tonConversionFactor);
  }, [tonConversionFactor, setValue]);

  const productType = product.material.type as ProductType;

  const shouldShowSelectForApplicationRate =
    productType.toUpperCase() === ProductType.ASPHALT ||
    productType.toUpperCase() === ProductType.ASPHALT_CURBING ||
    productType.toUpperCase() === ProductType.EXCAVATE_OUT;

  const applicationRateOptions = shouldShowSelectForApplicationRate
    ? applicationRateMappings[productType.toUpperCase() as ProductType] || []
    : [];

  useEffect(() => {
    const uppercaseProductType = productType.toUpperCase() as ProductType;
    const calculationFn = materialNeededCalculations[uppercaseProductType];
    if (!calculationFn) return;

    const needsTonConversion = [
      ProductType.ASPHALT,
      ProductType.ASPHALT_CURBING,
      ProductType.EXCAVATE_OUT,
    ];
    const needsApplicationRate = [
      ProductType.CONCRETE_FLATWORK,
      ProductType.MICROSURFACING,
      ProductType.GRADING,
      ProductType.SLURRY_SEAL,
      ProductType.CONCRETE_WATERWAY,
    ];

    let result = 0;
    if (needsTonConversion.includes(uppercaseProductType)) {
      result = (calculationFn as any)(
        product,
        tonConversionFactor || 0,
        watchedApplicationRate || 0
      );
    } else if (needsApplicationRate.includes(uppercaseProductType)) {
      result = (calculationFn as any)(product, watchedApplicationRate || 0);
    } else {
      result = (calculationFn as any)(product);
    }

    setCalculatedTons(result > 0 ? parseFloat(result.toFixed(2)) : 0);
  }, [
    product,
    productType,
    tonConversionFactor,
    watchedApplicationRate,
    watchedPlantId,
    watchedPlantProductId,
  ]);

  useEffect(() => {
    if (watchedPlantId) {
      setValue("plantProductId", "");
      setSelectedPlantProductId("");
    }
  }, [watchedPlantId, setValue]);

  const onSubmit = async (data: any) => {
    const result = await addPlantProduct({
      variables: {
        input: {
          proposalProductId: product.id,
          plantId: selectedPlantId,
          plantProductId: data.plantProductId,
          applicationRate: data.applicationRate,
          materialNeededOverride: data.materialNeeded ?? null,
          rateOverride: data.rate ? new Decimal(data.rate).times(100).round().toNumber() : null,
        },
      },
    });
    if (result.data) {
      reset();
      setSelectedPlantId("");
      setSelectedPlantProductId("");
      setPlantProductRate(null);
      onClose();
    }
  };

  const handleClose = () => {
    reset();
    setSelectedPlantId("");
    setSelectedPlantProductId("");
    setPlantProductRate(null);
    onClose();
  };

  const autocompleteFieldSx = {
    "& .MuiInput-root, & .MuiOutlinedInput-root": {
      fontSize: "12px",
      fontWeight: 400,
      color: draftColors.textPrimary,
      borderRadius: "8px",
      border: `1px solid ${draftColors.borderInput}`,
      padding: "4px 8px !important",
      "&::before, &::after": { display: "none" },
    },
    "& .MuiInputBase-input": { padding: "2px 0 !important", fontSize: "12px" },
  } as const;

  const depthSelectSx = {
    "& .MuiInput-root, & .MuiOutlinedInput-root, & .MuiSelect-root": {
      fontSize: "12px",
      fontWeight: 400,
      color: draftColors.textPrimary,
      borderRadius: "8px",
      border: `1px solid ${draftColors.borderInput}`,
      padding: "4px 8px !important",
      "&::before, &::after": { display: "none" },
    },
    "& .MuiInputBase-input": { padding: "2px 0 !important", fontSize: "12px" },
    "& .MuiFormLabel-root": { display: "none" },
    "& .MuiInputLabel-root": { display: "none" },
  } as const;

  return (
    <PlantOptions>
      {({
        plantOptions,
        plantProductOptions,
        loading: plantsLoading,
        search,
        plants,
        pagination,
      }) => (
        <MDBox component="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing="12px">
            <Grid item xs={6}>
              <UiTypography uiVariant="label-xs" sx={{ mb: "4px" }}>
                Plant
              </UiTypography>
              <MDBox sx={autocompleteFieldSx}>
                <Controller
                  name="plantId"
                  control={control}
                  rules={{ required: "Plant is required" }}
                  render={({ field, fieldState: { error } }) => (
                    <ScrollAsyncAutocomplete
                      options={plantOptions}
                      value={{
                        label: plantOptions.find((x) => x.value === field.value)?.label ?? "",
                        value: field.value,
                      }}
                      onChange={(value) => {
                        setSelectedPlantId(String(value));
                        field.onChange(value);
                      }}
                      loading={plantsLoading}
                      error={error?.message ?? ""}
                      label=""
                      search={search}
                      onLoadMore={pagination.loadMore}
                      hasMore={pagination.hasNextPage}
                      variant="standard"
                    />
                  )}
                />
              </MDBox>
            </Grid>

            <Grid item xs={6}>
              <UiTypography uiVariant="label-xs" sx={{ mb: "4px" }}>
                Plant Product
              </UiTypography>
              <MDBox sx={autocompleteFieldSx}>
                <Controller
                  name="plantProductId"
                  control={control}
                  rules={{ required: "Plant product is required" }}
                  render={({ field, fieldState: { error } }) => {
                    const ppOptions = (plantProductOptions as any)[selectedPlantId] ?? [];
                    return (
                      <ScrollAsyncAutocomplete
                        options={ppOptions}
                        value={{
                          label: ppOptions.find((x: any) => x.value === field.value)?.label ?? "",
                          value: field.value ?? "",
                        }}
                        onChange={async (value) => {
                          setSelectedPlantProductId(String(value));
                          field.onChange(value);
                          const selectedPlant = plants.find((p: any) => p.id === selectedPlantId);
                          if (selectedPlant) {
                            const pp = selectedPlant.products.find((p: any) => p.id === value);
                            if (pp) setPlantProductRate(pp.rate);
                          }
                        }}
                        loading={plantsLoading}
                        error={error?.message ?? ""}
                        label=""
                        search={() => {}}
                        onLoadMore={() => {}}
                        hasMore={false}
                        variant="standard"
                      />
                    );
                  }}
                />
              </MDBox>
            </Grid>

            <Grid item xs={6}>
              {shouldShowSelectForApplicationRate ? (
                <MDBox>
                  <UiTypography uiVariant="label-xs" sx={{ mb: "4px" }}>
                    Depth
                  </UiTypography>
                  <MDBox sx={depthSelectSx}>
                    <CustomSelect
                      label=""
                      control={control}
                      fullWidth
                      selectVariant="standard"
                      data={applicationRateOptions}
                      name="applicationRate"
                    />
                  </MDBox>
                </MDBox>
              ) : (
                <UiFieldGroup
                  label="Depth"
                  value={String((product.material.attributes as any)?.applicationRate ?? "")}
                  placeholder="0"
                  inputProps={register("applicationRate", NUMERIC_SET_VALUE_AS)}
                />
              )}
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="materialNeeded"
                control={control}
                render={({ field }) => (
                  <UiFieldGroup
                    label="Tons Needed"
                    value=""
                    placeholder={calculatedTons ? String(calculatedTons) : "0"}
                    inputProps={{
                      type: "number",
                      step: "0.01",
                      value: field.value ?? "",
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        const v = e.target.value;
                        field.onChange(v === "" ? null : parseFloat(v));
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="rate"
                control={control}
                render={({ field }) => (
                  <UiFieldGroup
                    label="Rate"
                    value=""
                    placeholder={
                      plantProductRate != null ? (plantProductRate / 100).toFixed(2) : "0.00"
                    }
                    startAdornment="$"
                    inputProps={{
                      type: "number",
                      step: "0.01",
                      value: field.value ?? "",
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                        field.onChange(e.target.value),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <MDBox display="flex" justifyContent="flex-end" mt="16px" gap="8px">
            <UiButton uiVariant="outlined" onClick={handleClose}>
              Cancel
            </UiButton>
            <UiButton uiVariant="primary-blue" type="submit" disabled={loading}>
              {loading ? "Adding..." : "Add material"}
            </UiButton>
          </MDBox>
        </MDBox>
      )}
    </PlantOptions>
  );
}

export default function AddMaterialDrawer({ open, onClose, product }: AddMaterialDrawerProps) {
  const { requiresPlant } = useProposalProductDetails(product);

  return (
    <UiSideModal open={open} onClose={onClose} title="Add Material" width="598px">
      <MDBox p="24px" display="flex" flexDirection="column" height="100%">
        {requiresPlant ? (
          <PlantForm product={product} onClose={onClose} />
        ) : (
          <NoPlantForm product={product} onClose={onClose} />
        )}
      </MDBox>
    </UiSideModal>
  );
}
